import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, PowerOff, Power } from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Receipt } from "lucide-react";

export default function GastosCategorias() {
  const { hasPermission } = useAuth();
  const { isSuperAdmin, activeTenantId } = useTenant();
  const canEdit = hasPermission("expenses:edit");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", sort_order: 100, active: true });
  const [saving, setSaving] = useState(false);

  const tenantParam = isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {};

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/expense-categories", {
        params: { ...tenantParam, include_inactive: true },
      });
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId]);

  const openCreate = () => {
    setEditing(null);
    setForm({ code: "", name: "", sort_order: (items.length + 1) * 10, active: true });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ code: c.code, name: c.name, sort_order: c.sort_order, active: c.active });
    setDialogOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/expense-categories/${editing.id}`, {
          name: form.name,
          sort_order: Number(form.sort_order),
          active: form.active,
        });
        toast.success("Categoría actualizada");
      } else {
        await api.post(
          "/expense-categories",
          {
            code: form.code,
            name: form.name,
            sort_order: Number(form.sort_order),
            active: form.active,
          },
          { params: tenantParam }
        );
        toast.success("Categoría creada");
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/expense-categories/${c.id}`, { active: !c.active });
      toast.success(!c.active ? "Categoría activada" : "Categoría desactivada");
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="page-gastos-categorias">
      <PageHeader
        title="Categorías de gastos"
        description="Catálogo editable por tenant. Desactivar una categoría no afecta los gastos históricos (usan un snapshot inmutable)."
        actions={
          canEdit && (
            <Button className="btn-tenant" onClick={openCreate} data-testid="btn-new-category">
              <Plus className="h-4 w-4 mr-2" /> Nueva categoría
            </Button>
          )
        }
      />

      <Card className="border-border">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-8" />)}
            </div>
          ) : items.length === 0 ? (
            <EmptyState title="Sin categorías" description="Aún no hay categorías." icon={Receipt} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="text-right">Orden</TableHead>
                  <TableHead>Estatus</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} data-testid={`cat-row-${c.id}`}>
                    <TableCell className="font-mono text-xs">{c.code}</TableCell>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">{c.sort_order}</TableCell>
                    <TableCell>
                      <Badge className={c.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}>
                        {c.active ? "Activa" : "Inactiva"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {canEdit && (
                        <>
                          <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`cat-edit-${c.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => toggleActive(c)} data-testid={`cat-toggle-${c.id}`}>
                            {c.active ? <PowerOff className="h-4 w-4 text-red-600" /> : <Power className="h-4 w-4 text-emerald-600" />}
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="cat-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar categoría" : "Nueva categoría"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Código *</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                disabled={!!editing}
                className="font-mono"
                data-testid="cat-code"
              />
              {editing && <p className="text-[10px] text-muted-foreground">El código no se puede modificar (usado como snapshot en gastos históricos).</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Orden</Label>
              <Input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                data-testid="cat-order"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button className="btn-tenant" onClick={submit} disabled={saving || !form.code || !form.name} data-testid="cat-save">
              {saving ? "Guardando…" : editing ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
